import type { ProxyAuditEntry } from '@/types/api'
import { parseDecimalString } from '@/lib/chart-transformers'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Info, Layers, AlertCircle } from 'lucide-react'

interface ProxyAuditPanelProps {
  auditEntries: ProxyAuditEntry[]
}

export function ProxyAuditPanel({ auditEntries }: ProxyAuditPanelProps) {
  const hasEntries = auditEntries.length > 0

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-500" />
              Proxy Data Audit
            </CardTitle>
            <CardDescription>
              Positions using backward-spliced synthetic data
            </CardDescription>
          </div>
          {hasEntries && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              {auditEntries.length} position{auditEntries.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Transparency Note */}
        <div className="bg-blue-50 rounded-lg p-4 text-sm">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 mb-1">
                About Proxy Data
              </p>
              <p className="text-blue-800">
                These positions use backward-spliced synthetic data for periods before their listing date.
                Beta-adjusted proxy returns are used with Blume adjustment (weight=0.67) to provide
                reasonable estimates of pre-listing behavior.
              </p>
            </div>
          </div>
        </div>

        {/* Audit Table */}
        {hasEntries ? (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Proxy Used</TableHead>
                  <TableHead>Splice Date</TableHead>
                  <TableHead>Blend Weight</TableHead>
                  <TableHead>Rationale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditEntries.map((entry) => (
                  <TableRow key={entry.assetId}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{entry.symbol}</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertCircle className="h-4 w-4 text-amber-500 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>This asset uses synthetic data for early periods</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {entry.proxy_symbol}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(entry.splice_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">
                        {(parseDecimalString(entry.blend_weight) * 100).toFixed(0)}%
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-xs">
                      {entry.rationale}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Layers className="h-10 w-10 text-gray-300 mb-2" />
            <p className="text-gray-500 font-medium">No proxy data used</p>
            <p className="text-gray-400 text-sm text-center mt-1 max-w-md">
              All positions in this scenario have complete historical data for the analysis period.
              No backward-splicing or synthetic data was required.
            </p>
          </div>
        )}

        {/* Crypto Proxy Note */}
        {hasEntries && auditEntries.some(e =>
          ['BTC', 'ETH', 'SOL', 'ADA', 'DOT', 'AVAX'].includes(e.proxy_symbol)
        ) && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
            <strong>Note:</strong> BTC is the preferred proxy for crypto assets due to its longest history
            and deep liquidity. Per Decision 12 in the project spec, BTC serves as the primary reference
            for backward-splicing newer cryptocurrency assets.
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface EmptyProxyAuditPanelProps {
  reason?: 'no-data' | 'loading'
}

export function EmptyProxyAuditPanel({ reason = 'no-data' }: EmptyProxyAuditPanelProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-500" />
          Proxy Data Audit
        </CardTitle>
        <CardDescription>Positions using backward-spliced synthetic data</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
          <Layers className="h-10 w-10 text-gray-300 mb-2" />
          <p className="text-gray-500">
            {reason === 'loading' ? 'Loading proxy audit data...' : 'No proxy data available'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
